import { Paperclip } from "lucide-react";
import { Fragment, type ReactNode } from "react";

import { fileTypeIcon } from "@/lib/attachments";
import type { AttachmentMeta, RichDoc } from "@/lib/board";
import { safeHref } from "@/lib/safeHref";
import { cn } from "@/lib/utils";

import { ImageThumb } from "./ImageThumb";
import { autolink, LINK_CLASS } from "./markdown";

/**
 * Read-only renderer for a Greenlight comment: its rich-text doc (TipTap JSON)
 * plus any attachments. Mirrors the editor's mark set (bold/italic/code/link +
 * lists) with a lightweight JSON→React walk — no editor instance per card. A
 * comment with no `rich` falls back to plain text + autolinking (legacy rows).
 * Attachments render as inline images (from Greenlight's own serve URL, which is
 * same-origin and always loads) or file chips, matching the ClickUp-thread renderer.
 */
type RichNode = {
  type?: string;
  text?: string;
  content?: RichNode[];
  marks?: { type?: string; attrs?: { href?: string } }[];
};

function renderInline(nodes: RichNode[] | undefined, keyBase: string): ReactNode[] {
  return (nodes ?? []).map((n, i) => {
    const key = `${keyBase}-${i}`;
    if (n.type === "hardBreak") return <br key={key} />;
    if (n.type !== "text" || !n.text) return null;
    const marks = n.marks ?? [];
    let el: ReactNode = n.text;
    if (marks.some((m) => m.type === "code")) {
      el = (
        <code className="rounded bg-muted px-1 py-0.5 text-xs">{el}</code>
      );
    }
    const bold = marks.some((m) => m.type === "bold");
    const italic = marks.some((m) => m.type === "italic");
    if (bold || italic) {
      el = (
        <span className={cn(bold && "font-semibold", italic && "italic")}>
          {el}
        </span>
      );
    }
    // Validate the protocol before rendering — a stored rich doc could carry a
    // javascript:/data: href; drop it to a plain (unlinked) span if unsafe.
    const href = safeHref(marks.find((m) => m.type === "link")?.attrs?.href);
    if (href) {
      el = (
        <a href={href} target="_blank" rel="noreferrer" className={LINK_CLASS}>
          {el}
        </a>
      );
    }
    return <Fragment key={key}>{el}</Fragment>;
  });
}

function renderBlocks(content: RichNode[], keyBase: string): ReactNode[] {
  return content.map((node, i) => {
    const key = `${keyBase}-${i}`;
    if (node.type === "bulletList" || node.type === "orderedList") {
      const items = (node.content ?? []).map((li, j) => (
        <li key={`${key}-${j}`}>
          {(li.content ?? []).flatMap((p, k) =>
            renderInline(p.content, `${key}-${j}-${k}`),
          )}
        </li>
      ));
      return node.type === "bulletList" ? (
        <ul key={key} className="my-1 list-disc pl-5">
          {items}
        </ul>
      ) : (
        <ol key={key} className="my-1 list-decimal pl-5">
          {items}
        </ol>
      );
    }
    // paragraph (and any unknown block) → a line of inline content
    return (
      <p key={key} className="my-1 first:mt-0 last:mb-0">
        {renderInline(node.content, key)}
      </p>
    );
  });
}

export function RichCommentBody({
  rich,
  fallback,
  attachments,
}: {
  rich: RichDoc | null;
  fallback: string;
  attachments: AttachmentMeta[];
}) {
  const content = (rich?.content as RichNode[] | undefined) ?? [];
  return (
    <div className="text-sm leading-relaxed text-foreground/90">
      {content.length > 0 ? (
        <div className="break-words">{renderBlocks(content, "r")}</div>
      ) : (
        <span className="whitespace-pre-wrap break-words">
          {autolink(fallback, "f")}
        </span>
      )}
      {attachments.length > 0 && <Attachments attachments={attachments} />}
    </div>
  );
}

/** Greenlight-stored attachments: inline image previews + file chips. */
function Attachments({ attachments }: { attachments: AttachmentMeta[] }) {
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((a) => {
        // Always render from Greenlight's own serve URL: the bytes live in our DB
        // and it authenticates with the session cookie. The ClickUp CDN url needs
        // a ClickUp session, so it 401s in an <img>/link loaded from here.
        const href = a.url;
        if (a.isImage) {
          return (
            <ImageThumb key={a.id} thumbSrc={href} fullSrc={href} alt={a.filename} />
          );
        }
        const Icon = fileTypeIcon(a.mime) ?? Paperclip;
        return (
          <a
            key={a.id}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-foreground no-underline hover:bg-muted"
          >
            <Icon className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">{a.filename}</span>
          </a>
        );
      })}
    </div>
  );
}
